import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts';
import {
  Zap, Weight, Flame, TrendingUp, Package,
  DollarSign, HelpCircle, Clock, RefreshCw,
  AlertTriangle, Activity, Shield
} from 'lucide-react';
import { sellerStatsAPI } from '../services/api';
import { formatPrice } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';

const MOCK_DATA = { V: 40, M: 100, E: 2, S: 2000, details: { avg_prep_hours: 0.6, total_sales: 100, total_revenue: 45000, total_questions: 20, unanswered_questions: 2, active_products: 15 } };

const EvolutionaryDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await sellerStatsAPI.getEvolutionStats();
      if (data && (data.V !== undefined)) {
        setStats(data);
      } else {
        setStats(MOCK_DATA);
      }
    } catch {
      setStats(MOCK_DATA);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const V = stats?.V ?? 0;
  const M = stats?.M ?? 0;
  const E = stats?.E ?? 0;
  const S = stats?.S ?? 0;
  const d = stats?.details || {};

  const vPct = Math.min(V, 100);
  const gaugeData = [{ name: 'V', value: vPct, fill: V < 50 ? '#ef4444' : '#22c55e' }];

  const eLevel = E > 5 ? 'critical' : E > 2 ? 'warning' : 'stable';
  const eColor = { critical: 'text-red-400', warning: 'text-amber-400', stable: 'text-emerald-400' }[eLevel];
  const eBg = { critical: 'from-red-950/60 to-red-900/30 border-red-500/30', warning: 'from-amber-950/60 to-amber-900/30 border-amber-500/30', stable: 'from-emerald-950/60 to-emerald-900/30 border-emerald-500/30' }[eLevel];
  const eBarPct = Math.min((E / 10) * 100, 100);

  return (
    <div className="rounded-2xl bg-[#0a0a0f] p-4 sm:p-6 -m-1 space-y-6" data-testid="evo-dashboard">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight" data-testid="evo-dashboard-title">
                  Centro de Control Evolutivo
                </h1>
                <p className="text-gray-500 text-sm mt-1 font-mono">Motor de rendimiento algoritmico</p>
              </div>
              <button
                onClick={load}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-orange-400 hover:border-orange-500/40 transition-all text-sm"
                data-testid="evo-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
              </button>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center h-72 gap-3">
                <Activity className="w-10 h-10 text-orange-500 animate-pulse" />
                <p className="text-gray-500 font-mono text-sm">Calculando metricas evolutivas...</p>
              </div>
            ) : (
              <>
                {/* === ECUACION CENTRAL S === */}
                <div
                  className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#111118] via-[#0d0d14] to-[#0a0a10] border border-orange-500/20 p-6 sm:p-8"
                  data-testid="evo-score"
                >
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(249,115,22,0.08),transparent_60%)]" />
                  <div className="absolute top-0 right-0 w-48 h-48 bg-orange-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />

                  <div className="relative flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div className="text-center sm:text-left">
                      <p className="text-xs font-mono uppercase tracking-[0.25em] text-orange-400/70 mb-2">Salud Evolutiva</p>
                      <p className="text-6xl sm:text-7xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-300 to-amber-400 leading-none" data-testid="evo-score-value">
                        {S.toLocaleString()}
                      </p>
                      <div className="mt-3 inline-block px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                        <p className="text-xs font-mono text-gray-400">
                          S = (<span className="text-blue-400">Velocidad</span> x <span className="text-green-400">Masa</span>) / <span className="text-red-400">Entropia</span>
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-5 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-2">
                          <Zap className="w-6 h-6 text-blue-400" />
                        </div>
                        <p className="text-xl font-bold text-blue-400 font-mono" data-testid="evo-v-value">{V}</p>
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">Velocidad</p>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-14 h-14 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center mb-2">
                          <Weight className="w-6 h-6 text-green-400" />
                        </div>
                        <p className="text-xl font-bold text-green-400 font-mono" data-testid="evo-m-value">{M}</p>
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">Masa</p>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-14 h-14 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-2">
                          <Flame className="w-6 h-6 text-red-400" />
                        </div>
                        <p className="text-xl font-bold text-red-400 font-mono" data-testid="evo-e-value">{E}</p>
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">Entropia</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* === VELOCIMETRO + ENTROPIA row === */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                  {/* VELOCIMETRO DE FERMAT */}
                  <div
                    className="rounded-2xl bg-gradient-to-br from-[#111118] to-[#0d0d14] border border-blue-500/15 p-6"
                    data-testid="evo-velocity-gauge"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <Zap className="w-5 h-5 text-blue-400" />
                      <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Velocimetro de Fermat</h3>
                    </div>
                    <p className="text-xs text-gray-500 font-mono mb-4">Velocidad de reaccion logistica (V)</p>

                    <div className="flex flex-col items-center">
                      <div className="relative">
                        <RadialBarChart
                          width={220}
                          height={140}
                          cx={110}
                          cy={130}
                          innerRadius={70}
                          outerRadius={100}
                          barSize={14}
                          data={gaugeData}
                          startAngle={180}
                          endAngle={0}
                        >
                          <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                          <RadialBar
                            background={{ fill: '#1a1a2e' }}
                            dataKey="value"
                            angleAxisId={0}
                            cornerRadius={8}
                          />
                        </RadialBarChart>
                        <div className="absolute inset-0 flex flex-col items-center justify-end pb-3">
                          <p className={`text-3xl font-black font-mono ${vPct < 50 ? 'text-red-400' : 'text-green-400'}`}>
                            {V}
                          </p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider">vel/hora</p>
                        </div>
                      </div>

                      <div className="w-full flex justify-between text-[10px] text-gray-600 font-mono px-4 -mt-1">
                        <span>0</span>
                        <span>50</span>
                        <span>100</span>
                      </div>

                      {/* Alerta condicional: V < 50 */}
                      {V < 50 && (
                        <div className="mt-4 w-full rounded-lg bg-red-950/50 border border-red-500/30 px-4 py-3 flex items-start gap-2" data-testid="evo-velocity-alert">
                          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                          <p className="text-xs text-red-300 leading-relaxed">
                            Tu inercia esta frenando tu crecimiento exponencial (Aumentando Masa Estatica)
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-2 gap-3 text-center">
                      <div>
                        <p className="text-lg font-bold text-white font-mono">{d.avg_prep_hours || 24}h</p>
                        <p className="text-[10px] text-gray-500 uppercase">Tiempo promedio</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-white font-mono">{d.total_sales || 0}</p>
                        <p className="text-[10px] text-gray-500 uppercase">Ordenes procesadas</p>
                      </div>
                    </div>
                  </div>

                  {/* MONITOR DE ENTROPIA */}
                  <div
                    className={`rounded-2xl bg-gradient-to-br ${eBg} border p-6`}
                    data-testid="evo-entropy-monitor"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <Flame className={`w-5 h-5 ${eColor}`} />
                      <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Monitor de Entropia</h3>
                    </div>
                    <p className="text-xs text-gray-500 font-mono mb-6">Nivel de friccion y caos del sistema (E)</p>

                    <div className="flex flex-col items-center gap-4">
                      <div className="relative w-32 h-32">
                        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                          <circle cx="50" cy="50" r="42" fill="none" stroke="#1a1a2e" strokeWidth="8" />
                          <circle
                            cx="50" cy="50" r="42" fill="none"
                            stroke={E > 5 ? '#ef4444' : E > 2 ? '#f59e0b' : '#22c55e'}
                            strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray={`${eBarPct * 2.64} ${264 - eBarPct * 2.64}`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <p className={`text-3xl font-black font-mono ${eColor}`} data-testid="evo-entropy-value">{E}</p>
                          <p className="text-[10px] text-gray-500 uppercase">Entropia</p>
                        </div>
                      </div>

                      <div className="w-full flex items-center justify-between text-[10px] font-mono px-1">
                        <span className="text-emerald-500">0 Estable</span>
                        <span className="text-amber-500">2-5 Alerta</span>
                        <span className="text-red-500">5+ Critico</span>
                      </div>

                      {/* Alerta condicional: E > 5 */}
                      {E > 5 && (
                        <div className="w-full rounded-lg bg-red-950/60 border border-red-500/40 px-4 py-3 flex items-start gap-2" data-testid="evo-entropy-alert">
                          <Shield className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                          <p className="text-xs text-red-300 leading-relaxed">
                            Peligro: Nivel de Caos alto. La Ley de Murphy predice friccion inminente en el sistema
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="mt-5 pt-4 border-t border-white/5 grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-lg font-bold text-white font-mono">{d.total_questions || 0}</p>
                        <p className="text-[10px] text-gray-500 uppercase">Preguntas</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-red-400 font-mono">{d.unanswered_questions || 0}</p>
                        <p className="text-[10px] text-gray-500 uppercase">Sin respuesta</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-white font-mono">
                          {d.total_questions ? Math.round(((d.total_questions - d.unanswered_questions) / d.total_questions) * 100) : 100}%
                        </p>
                        <p className="text-[10px] text-gray-500 uppercase">Resp. rate</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* === DETAIL METRICS === */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="rounded-xl bg-[#111118] border border-white/5 p-4" data-testid="evo-metric-time">
                    <Clock className="w-5 h-5 text-blue-400 mb-2" />
                    <p className="text-2xl font-bold text-white font-mono">{d.avg_prep_hours || 24}h</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">Tiempo preparacion</p>
                  </div>
                  <div className="rounded-xl bg-[#111118] border border-white/5 p-4" data-testid="evo-metric-sales">
                    <TrendingUp className="w-5 h-5 text-green-400 mb-2" />
                    <p className="text-2xl font-bold text-white font-mono">{d.total_sales || 0}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">Ventas totales</p>
                  </div>
                  <div className="rounded-xl bg-[#111118] border border-white/5 p-4" data-testid="evo-metric-revenue">
                    <DollarSign className="w-5 h-5 text-amber-400 mb-2" />
                    <p className="text-2xl font-bold text-white font-mono">{formatPrice(d.total_revenue || 0)}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">Ingresos</p>
                  </div>
                  <div className="rounded-xl bg-[#111118] border border-white/5 p-4" data-testid="evo-metric-products">
                    <Package className="w-5 h-5 text-purple-400 mb-2" />
                    <p className="text-2xl font-bold text-white font-mono">{d.active_products || 0}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">Productos activos</p>
                  </div>
                </div>

                {/* === QUICK ACTIONS === */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { icon: Package, label: 'Mis Productos', path: '/seller/products', id: 'evo-nav-products' },
                    { icon: DollarSign, label: 'Mis Ventas', path: '/seller/orders', id: 'evo-nav-orders' },
                    { icon: HelpCircle, label: 'Preguntas', path: '/seller/questions', id: 'evo-nav-questions' },
                    { icon: TrendingUp, label: 'Campanas', path: '/advertiser/campaigns', id: 'evo-nav-campaigns' },
                  ].map(({ icon: Icon, label, path, id }) => (
                    <button
                      key={path}
                      onClick={() => navigate(path)}
                      className="flex flex-col items-center gap-2 py-4 rounded-xl bg-white/[0.03] border border-white/5 text-gray-400 hover:text-orange-400 hover:border-orange-500/30 hover:bg-orange-500/5 transition-all"
                      data-testid={id}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
    </div>
  );
};

export default EvolutionaryDashboard;
